import { useState, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Receipt,
  HardHat,
  Globe,
  Target,
  TrendingUp,
  Handshake,
  Wallet,
  Percent,
  Scale,
  PiggyBank,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  Landmark,
  Users,
  Users2,
  Briefcase,
  FileBarChart,
  PieChart,
  Building2,
  Activity,
} from "lucide-react";
import { getRoleFromToken } from "../api";
import {
  DEPARTAMENTO_POR_ROLE,
  DEPARTAMENTOS_DESPESAS_GERAIS,
  DEPARTAMENTOS_CUSTOS_GERAIS,
  departamentosVisiveis,
} from "../constants/departamentosOrcamento";
import { ROLES_PREMISSAS_FATURAMENTO } from "../constants/premissasFaturamento";
import { ROLES_FATURAMENTO_BRUTO } from "../constants/faturamentoBruto";
import { ROLES_NOVAS_VENDAS_ORCAMENTARIA } from "../constants/novasVendasOrcamento";
import { ROLES_CUSTO_NOVAS_VENDAS_ORCAMENTARIA } from "../constants/custoNovasVendas";
import { ROLES_DESPESAS_FINANCEIRAS } from "../constants/despesasFinanceiras";
import { ROLES_MAO_DE_OBRA } from "../constants/maoDeObra";
import { ROLES_IMPOSTOS } from "../constants/impostos";
import { ROLES_INVESTIMENTOS } from "../constants/investimentos";
import { ROLES_DRE_GERENCIAL } from "../constants/dreGerencial";
import { ROLES_ANALISE_DEPARTAMENTO } from "../constants/analiseDepartamento";
import { ROLES_DRE_EMPRESA } from "../constants/dreEmpresa";
import { ROLES_ALOCACAO_PESSOAL } from "../constants/alocacaoPessoal";
import { ROLES_VISAO_EXECUTIVA } from "../constants/visaoExecutiva";
import { ROLES_CUSTOS_GERAIS_PROJETO } from "../constants/custosGeraisProjeto";

// ─────────────────────────────────────────────────────────────
// ROLES COM ACESSO À SIDEBAR DE GESTÃO ORÇAMENTÁRIA
// Admin vê tudo; as demais seguem DEPARTAMENTO_POR_ROLE (cada uma só enxerga
// os painéis cujo(s) departamento(s) ela tem acesso — ver rolesComAcessoAoPainel).
// ─────────────────────────────────────────────────────────────
const ROLES_PERMITIDAS = ["ADMIN", ...Object.keys(DEPARTAMENTO_POR_ROLE)];

// ─────────────────────────────────────────────────────────────
// PAINÉIS DE NAVEGAÇÃO
// Novas visões do módulo entram aqui. `departamentosDoPainel` (opcional) restringe
// a exibição do item a quem tem acesso a pelo menos um desses departamentos;
// `rolesDoPainel` (opcional) restringe a roles específicas (ex: visão executiva).
// Sem nenhum dos dois = visível a todo mundo com acesso à sidebar (ex: "Geral").
// ─────────────────────────────────────────────────────────────
export const PAINEIS_GESTAO_ORCAMENTARIA = [
  {
    id: "geral",
    label: "Geral",
    path: "/gestao-orcamentaria/geral",
    icon: Globe,
    description: "Visão consolidada de todos os departamentos",
  },
  {
    id: "despesas-gerais",
    label: "Despesas Gerais",
    path: "/gestao-orcamentaria/despesas-gerais",
    icon: Receipt,
    description: "Planejamento de Despesas Gerais (demais departamentos)",
    departamentosDoPainel: DEPARTAMENTOS_DESPESAS_GERAIS,
  },
  {
    id: "custos-gerais",
    label: "Custos Gerais",
    path: "/gestao-orcamentaria/custos-gerais",
    icon: HardHat,
    description: "Planejamento de Custos Gerais (Operação)",
    departamentosDoPainel: DEPARTAMENTOS_CUSTOS_GERAIS,
  },
  {
    id: "premissas-faturamento",
    label: "Premissas de Faturamento",
    path: "/gestao-orcamentaria/premissas-faturamento",
    icon: Target,
    description: "Vendas, faturamento e margem bruta projetados (executivo)",
    rolesDoPainel: ROLES_PREMISSAS_FATURAMENTO,
  },
  {
    id: "faturamento-bruto",
    label: "Faturamento Bruto",
    path: "/gestao-orcamentaria/faturamento-bruto",
    icon: TrendingUp,
    description: "Carteira de projetos por empresa: faturamento, custo sem margem e líquido",
    rolesDoPainel: ROLES_FATURAMENTO_BRUTO,
  },
  {
    id: "novas-vendas",
    label: "Novas Vendas",
    path: "/gestao-orcamentaria/novas-vendas",
    icon: Handshake,
    description: "Carteira de novos contratos/aditivos e sua distribuição mensal",
    rolesDoPainel: ROLES_NOVAS_VENDAS_ORCAMENTARIA,
  },
  {
    id: "custo-novas-vendas",
    label: "Custo Novas Vendas",
    path: "/gestao-orcamentaria/custo-novas-vendas",
    icon: Wallet,
    description: "DRE Gerencial de custo projetado por categoria, associado às novas vendas do ano",
    rolesDoPainel: ROLES_CUSTO_NOVAS_VENDAS_ORCAMENTARIA,
  },
  {
    id: "custo-mao-de-obra",
    label: "Custo Mão de Obra",
    path: "/gestao-orcamentaria/custo-mao-de-obra",
    icon: Users,
    description: "Cadastro de colaboradores, encargos e benefícios com projeção de custo mensal (Operação)",
    rolesDoPainel: ROLES_MAO_DE_OBRA,
  },
  {
    id: "despesas-mao-de-obra",
    label: "Despesas Mão de Obra",
    path: "/gestao-orcamentaria/despesas-mao-de-obra",
    icon: Users,
    description: "Cadastro de colaboradores, encargos e benefícios com projeção de custo mensal (demais departamentos)",
    rolesDoPainel: ROLES_MAO_DE_OBRA,
  },
  {
    id: "alocacao-pessoal",
    label: "Alocação de Pessoal",
    path: "/gestao-orcamentaria/alocacao-pessoal",
    icon: Users2,
    description: "% de alocação mensal dos colaboradores da OPERAÇÃO por projeto",
    rolesDoPainel: ROLES_ALOCACAO_PESSOAL,
  },
  {
    id: "custos-gerais-projeto",
    label: "Custos Gerais por Projeto",
    path: "/gestao-orcamentaria/custos-gerais-projeto",
    icon: Briefcase,
    description: "Custos Gerais (Operação) reorganizados por projeto, com Viagens e rateio de Softwares",
    rolesDoPainel: ROLES_CUSTOS_GERAIS_PROJETO,
  },
  {
    id: "visao-executiva",
    label: "Visão Executiva",
    path: "/gestao-orcamentaria/visao-executiva",
    icon: Activity,
    description: "Saúde da alocação, ranking de custo de pessoal por projeto e ociosidade acumulada (Operação)",
    rolesDoPainel: ROLES_VISAO_EXECUTIVA,
  },

  {
    id: "despesas-financeiras",
    label: "Despesas Financeiras",
    path: "/gestao-orcamentaria/despesas-financeiras",
    icon: Percent,
    description: "Juros, IOF, tarifas bancárias e demais despesas financeiras (visão geral)",
    rolesDoPainel: ROLES_DESPESAS_FINANCEIRAS,
  },
  {
    id: "impostos",
    label: "Impostos",
    path: "/gestao-orcamentaria/impostos",
    icon: Scale,
    description: "IRPJ, CSLL e demais impostos (visão geral)",
    rolesDoPainel: ROLES_IMPOSTOS,
  },
  {
    id: "investimentos",
    label: "Investimentos",
    path: "/gestao-orcamentaria/investimentos",
    icon: PiggyBank,
    description: "Investimentos em terceiros, imobilizados, YouXLab e equipamentos (visão geral)",
    rolesDoPainel: ROLES_INVESTIMENTOS,
  },
  {
    id: "dre-gerencial",
    label: "DRE Gerencial",
    path: "/gestao-orcamentaria/dre-gerencial",
    icon: FileBarChart,
    description: "Consolidado de todos os módulos: faturamento, custos/despesas e resultado (somente leitura)",
    rolesDoPainel: ROLES_DRE_GERENCIAL,
  },
  {
    id: "analise-departamento",
    label: "Análise por Departamento",
    path: "/gestao-orcamentaria/analise-departamento",
    icon: PieChart,
    description: "Ranking de custos por departamento com detalhamento por plano de contas (somente leitura)",
    rolesDoPainel: ROLES_ANALISE_DEPARTAMENTO,
  },
  {
    id: "dre-empresa",
    label: "DRE por Empresa",
    path: "/gestao-orcamentaria/dre-empresa",
    icon: Building2,
    description: "DRE Gerencial aberto por empresa (GT/CIGTA/CRIA) — dado real onde já existe, rateio configurável onde ainda não existe",
    rolesDoPainel: ROLES_DRE_EMPRESA,
  },
];

// ─────────────────────────────────────────────────────────────
// GRUPOS DE NAVEGAÇÃO
// Agrupa os painéis acima seguindo a lógica do processo de FP&A (premissas →
// faturamento → custos/despesas → resultado). Cada grupo só aparece se tiver pelo
// menos 1 painel visível para a role atual — a ordem dentro do grupo é a definida
// aqui, não a ordem em PAINEIS_GESTAO_ORCAMENTARIA.
// ─────────────────────────────────────────────────────────────
export const GRUPOS_GESTAO_ORCAMENTARIA = [
  // { id: "visao-geral", label: "Visão Geral", paineis: ["geral"] },
  // {
  //   id: "faturamento",
  //   label: "Faturamento",
  //   paineis: ["premissas-faturamento", "faturamento-bruto", "novas-vendas", "custo-novas-vendas"],
  // },
  {
    id: "custos",
    label: "Custos",
    paineis: ["custos-gerais", "custo-mao-de-obra", "alocacao-pessoal", , "custos-gerais-projeto","visao-executiva"],
  },
  {
    id: "despesas",
    label: "Despesas",
    paineis: ["despesas-gerais", "despesas-mao-de-obra", "despesas-financeiras"],
  },
  { id: "resultado", label: "Resultado", paineis: ["impostos", "investimentos", "dre-gerencial", "analise-departamento", "dre-empresa"] },
];

// ─────────────────────────────────────────────────────────────
// THEME TOKENS
// ─────────────────────────────────────────────────────────────
const SIDEBAR_THEME = {
  dark: {
    bg: "#0F1117",
    bgHover: "#1A1D27",
    bgActive: "#1E2235",
    border: "rgba(255,255,255,0.07)",
    text: "#E2E8F0",
    muted: "#64748B",
    activeText: "#FFFFFF",
    activeBorder: "#6366F1",
    brand: "#6366F1",
    brandLight: "rgba(99,102,241,0.15)",
    shadow: "2px 0 24px rgba(0,0,0,0.4)",
    tooltipBg: "#1E2235",
    headerBg: "rgba(99,102,241,0.12)",
    scrollbar: "#1E2235",
  },
  light: {
    bg: "#FFFFFF",
    bgHover: "#F1F5F9",
    bgActive: "#EEF2FF",
    border: "rgba(0,0,0,0.08)",
    text: "#1E293B",
    muted: "#94A3B8",
    activeText: "#4338CA",
    activeBorder: "#4338CA",
    brand: "#4338CA",
    brandLight: "rgba(67,56,202,0.08)",
    shadow: "2px 0 16px rgba(0,0,0,0.08)",
    tooltipBg: "#1E293B",
    headerBg: "rgba(67,56,202,0.06)",
    scrollbar: "#E2E8F0",
  },
};

// ─────────────────────────────────────────────────────────────
// SIDEBAR COMPONENT
// Props:
//   isLight                 (bool)     — segue o tema do painel pai
//   onNavigate               (fn)      — callback após navegação (opcional)
//   departamentos            (array)   — departamentos disponíveis no painel atual (opcional; 1 item = não exibe seletor)
//   departamentoAtual        (string)  — departamento selecionado no momento
//   onSelecionarDepartamento (fn)      — chamada com o novo departamento ao clicar em um item da lista
// ─────────────────────────────────────────────────────────────
export default function SideBarNavGestaoOrcamentaria({
  isLight = false,
  onNavigate,
  departamentos,
  departamentoAtual,
  onSelecionarDepartamento,
}) {
  const [collapsed, setCollapsed] = useState(false);
  const [hoveredId, setHoveredId] = useState(null);
  const [tooltipPos, setTooltipPos] = useState({ top: 0 });
  const [depAberto, setDepAberto] = useState(false);
  const itemRefs = useRef({});

  const navigate = useNavigate();
  const location = useLocation();

  const roleRaw = getRoleFromToken() ?? "";
  const roleAtual = roleRaw.toUpperCase();
  const T = isLight ? SIDEBAR_THEME.light : SIDEBAR_THEME.dark;

  if (!ROLES_PERMITIDAS.includes(roleAtual)) return null;

  // Só mostra o painel se a role tiver acesso a pelo menos 1 dos departamentos daquele
  // painel, ou estiver na lista de roles do painel — painéis sem nenhum dos dois
  // (ex: "Geral") ficam sempre visíveis.
  const departamentosDaRole = departamentosVisiveis(roleRaw);
  const paineisVisiveis = PAINEIS_GESTAO_ORCAMENTARIA.filter((painel) => {
    if (painel.departamentosDoPainel) return painel.departamentosDoPainel.some((d) => departamentosDaRole.includes(d));
    if (painel.rolesDoPainel) return painel.rolesDoPainel.includes(roleRaw);
    return true;
  });

  // Painéis visíveis organizados por grupo (ver GRUPOS_GESTAO_ORCAMENTARIA) — grupo some
  // inteiro se nenhum dos seus painéis estiver visível para a role atual.
  const paineisVisiveisIds = new Set(paineisVisiveis.map((p) => p.id));
  const painelPorId = new Map(PAINEIS_GESTAO_ORCAMENTARIA.map((p) => [p.id, p]));
  const gruposVisiveis = GRUPOS_GESTAO_ORCAMENTARIA
    .map((grupo) => ({
      ...grupo,
      paineis: grupo.paineis.map((id) => painelPorId.get(id)).filter((p) => p && paineisVisiveisIds.has(p.id)),
    }))
    .filter((grupo) => grupo.paineis.length > 0);

  const isActive = (path) => location.pathname === path;

  const handleNav = (path) => {
    navigate(path);
    if (onNavigate) onNavigate(path);
  };

  const WIDTH_EXPANDED = 220;
  const WIDTH_COLLAPSED = 62;
  const width = collapsed ? WIDTH_COLLAPSED : WIDTH_EXPANDED;

  return (
    <>
      <style>{`
        .snav-gestorc-item { transition: background 0.15s, color 0.15s; }
        .snav-gestorc-item:hover { background: var(--snav-gestorc-hover) !important; }
        .snav-gestorc-item:active { transform: scale(0.98); }
        .snav-gestorc-label {
          transition: opacity 0.18s, width 0.18s, transform 0.18s;
          white-space: nowrap;
          overflow: hidden;
        }
        .snav-gestorc-tooltip {
          animation: tooltipInGestorc 0.12s ease forwards;
        }
        @keyframes tooltipInGestorc {
          from { opacity:0; transform:translateX(-4px); }
          to   { opacity:1; transform:translateX(0); }
        }
        .snav-gestorc-toggle { transition: background 0.15s, transform 0.2s; }
        .snav-gestorc-toggle:hover { background: var(--snav-gestorc-hover) !important; }
        .snav-gestorc-section-label { transition: opacity 0.18s; }
      `}</style>

      <aside
        style={{
          "--snav-gestorc-hover": T.bgHover,
          width,
          minWidth: width,
          maxWidth: width,
          height: "100vh",
          background: T.bg,
          borderRight: `1px solid ${T.border}`,
          boxShadow: T.shadow,
          display: "flex",
          flexDirection: "column",
          transition:
            "width 0.22s cubic-bezier(0.4,0,0.2,1), min-width 0.22s cubic-bezier(0.4,0,0.2,1)",
          position: "sticky",
          top: 0,
          zIndex: 40,
          overflow: "hidden",
          fontFamily: "'DM Sans','Segoe UI',sans-serif",
        }}
      >
        {/* ── Logo / Header ── */}
        <div
          style={{
            padding: collapsed ? "18px 0" : "18px 16px",
            borderBottom: `1px solid ${T.border}`,
            background: T.headerBg,
            display: "flex",
            alignItems: "center",
            justifyContent: collapsed ? "center" : "space-between",
            minHeight: 62,
            gap: 10,
          }}
        >
          {!collapsed && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  width: 30,
                  height: 30,
                  borderRadius: 8,
                  background: T.brand,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <Landmark size={15} color="#fff" />
              </div>
              <div style={{ overflow: "hidden" }}>
                <div
                  style={{
                    fontSize: 13,
                    fontWeight: 700,
                    color: T.text,
                    letterSpacing: "-0.01em",
                    lineHeight: 1.2,
                  }}
                >
                  YOUX GROUP
                </div>
                <div style={{ fontSize: 10, color: T.muted, fontWeight: 500 }}>
                  Gestão Orçamentária
                </div>
              </div>
            </div>
          )}

          {collapsed && (
            <div
              style={{
                width: 32,
                height: 32,
                borderRadius: 8,
                background: T.brand,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Landmark size={15} color="#fff" />
            </div>
          )}
        </div>

        {/* ── Seção: Painéis ── */}
        <div
          style={{
            flex: 1,
            overflowY: "auto",
            padding: "10px 8px",
            overflowX: "hidden",
          }}
        >
          {gruposVisiveis.map((grupo, grupoIdx) => (
            <div key={grupo.id} style={{ marginTop: grupoIdx === 0 ? 0 : 14 }}>
              {!collapsed ? (
                <div
                  className="snav-gestorc-section-label"
                  style={{
                    fontSize: 10,
                    fontWeight: 700,
                    letterSpacing: "0.1em",
                    color: T.muted,
                    textTransform: "uppercase",
                    padding: "6px 8px 8px",
                  }}
                >
                  {grupo.label}
                </div>
              ) : (
                grupoIdx > 0 && <div style={{ height: 1, background: T.border, margin: "8px 10px" }} />
              )}

              {grupo.paineis.map((painel) => {
                const Icon = painel.icon;
                const active = isActive(painel.path);
                const hovered = hoveredId === painel.id;
                // O dropdown de departamentos acompanha o painel ativo (cada página só conhece os
                // seus próprios departamentos) — assim, se "Custos Gerais" ganhar mais opções no
                // futuro além de Operação, o dropdown já aparece sem precisar mexer aqui de novo.
                const temDropdownDepartamento = active && departamentos && departamentos.length > 0;

                return (
                  <div key={painel.id}>
                    <div
                      ref={(el) => (itemRefs.current[painel.id] = el)}
                      className="snav-gestorc-item"
                      onMouseEnter={() => {
                        setHoveredId(painel.id);
                        if (collapsed && itemRefs.current[painel.id]) {
                          const rect =
                            itemRefs.current[painel.id].getBoundingClientRect();
                          setTooltipPos({ top: rect.top + rect.height / 2 });
                        }
                      }}
                      onMouseLeave={() => setHoveredId(null)}
                      onClick={() => handleNav(painel.path)}
                      title={collapsed ? painel.label : undefined}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 10,
                        padding: collapsed ? "10px 0" : "10px 10px",
                        borderRadius: 8,
                        cursor: "pointer",
                        marginBottom: 2,
                        justifyContent: collapsed ? "center" : "flex-start",
                        background: active ? T.bgActive : "transparent",
                        borderLeft: active
                          ? `3px solid ${T.activeBorder}`
                          : "3px solid transparent",
                        position: "relative",
                        userSelect: "none",
                      }}
                    >
                      <Icon
                        size={18}
                        color={active ? T.brand : hovered ? T.text : T.muted}
                        strokeWidth={active ? 2.2 : 1.8}
                        style={{ flexShrink: 0, transition: "color 0.15s" }}
                      />

                      {!collapsed && (
                        <div
                          className="snav-gestorc-label"
                          style={{
                            flex: 1,
                            fontSize: 13,
                            fontWeight: active ? 600 : 500,
                            color: active ? T.activeText : T.text,
                            letterSpacing: "-0.01em",
                          }}
                        >
                          {painel.label}
                        </div>
                      )}

                      {!collapsed && temDropdownDepartamento && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setDepAberto((v) => !v);
                          }}
                          title={depAberto ? "Recolher departamentos" : "Expandir departamentos"}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            background: "none",
                            border: "none",
                            cursor: "pointer",
                            padding: 2,
                            color: active ? T.brand : T.muted,
                            flexShrink: 0,
                          }}
                        >
                          {depAberto ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                        </button>
                      )}

                      {collapsed && active && (
                        <div
                          style={{
                            position: "absolute",
                            right: 6,
                            top: "50%",
                            transform: "translateY(-50%)",
                            width: 5,
                            height: 5,
                            borderRadius: "50%",
                            background: T.brand,
                          }}
                        />
                      )}
                    </div>

                    {/* ── Dropdown: Departamentos (aninhado em "Despesas Gerais") ── */}
                    {!collapsed && temDropdownDepartamento && depAberto && (
                      <div style={{ paddingLeft: 14, marginBottom: 4 }}>
                        {departamentos.map((dep) => {
                          const depActive = dep === departamentoAtual;
                          return (
                            <div
                              key={dep}
                              className="snav-gestorc-item"
                              onClick={() => onSelecionarDepartamento && onSelecionarDepartamento(dep)}
                              style={{
                                padding: "7px 10px",
                                borderRadius: 8,
                                cursor: "pointer",
                                marginBottom: 2,
                                background: depActive ? T.bgActive : "transparent",
                                borderLeft: depActive ? `3px solid ${T.activeBorder}` : "3px solid transparent",
                                userSelect: "none",
                              }}
                            >
                              <div
                                style={{
                                  fontSize: 11.5,
                                  lineHeight: 1.3,
                                  fontWeight: depActive ? 700 : 500,
                                  color: depActive ? T.activeText : T.text,
                                  letterSpacing: "-0.01em",
                                }}
                              >
                                {dep}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ))}
        </div>

        {/* ── Botão Colapsar ── */}
        <div
          style={{
            borderTop: `1px solid ${T.border}`,
            padding: "10px 8px",
          }}
        >
          <button
            className="snav-gestorc-toggle"
            onClick={() => setCollapsed((v) => !v)}
            style={{
              "--snav-gestorc-hover": T.bgHover,
              display: "flex",
              alignItems: "center",
              justifyContent: collapsed ? "center" : "flex-start",
              gap: 8,
              width: "100%",
              padding: collapsed ? "9px 0" : "9px 10px",
              borderRadius: 8,
              border: "none",
              background: "transparent",
              cursor: "pointer",
              color: T.muted,
              fontSize: 12,
              fontWeight: 500,
              fontFamily: "inherit",
            }}
          >
            {collapsed ? (
              <ChevronRight size={16} color={T.muted} />
            ) : (
              <>
                <ChevronLeft size={16} color={T.muted} />
                <span>Recolher</span>
              </>
            )}
          </button>
        </div>

        {/* ── Tooltip flutuante (modo colapsado) ── */}
        {collapsed &&
          hoveredId &&
          (() => {
            const painel = paineisVisiveis.find((p) => p.id === hoveredId);
            if (!painel) return null;
            return (
              <div
                className="snav-gestorc-tooltip"
                style={{
                  position: "fixed",
                  left: WIDTH_COLLAPSED + 12,
                  top: tooltipPos.top,
                  transform: "translateY(-50%)",
                  background: T.tooltipBg,
                  color: "#fff",
                  fontSize: 12,
                  fontWeight: 600,
                  padding: "6px 12px",
                  borderRadius: 7,
                  pointerEvents: "none",
                  zIndex: 9999,
                  whiteSpace: "nowrap",
                  boxShadow: "0 4px 16px rgba(0,0,0,0.3)",
                  fontFamily: "'DM Sans','Segoe UI',sans-serif",
                }}
              >
                {painel.label}
                <div
                  style={{
                    fontSize: 10,
                    fontWeight: 400,
                    color: "rgba(255,255,255,0.6)",
                    marginTop: 1,
                  }}
                >
                  {painel.description}
                </div>
              </div>
            );
          })()}
      </aside>
    </>
  );
}
